import React, { useMemo } from 'react';
import { createPortal } from 'react-dom';
import { uuidv4 } from '@graphistry/node-util/uuid';

import { useSetEditor } from './useSetEditor.ts';
import { GfqlBuilder } from './GfqlBuilder.tsx';
import { GfqlRawEditor } from './GfqlRawEditor.tsx';
import type {
  SetExpr,
  Set,
  InteractionDetails,
  Collections
} from './types.ts';

import styles from './styles.less';

interface SetModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (result: { value: Record<string, Set> }) => void;
  collections: Collections;
  interactionDetails: InteractionDetails;
  initialName?: string;
  initialGfql?: string;
  initialDescription?: string;
}

interface CreateSetParams {
  type: string;
  dataType: string;
  nodes?: any;
  edges?: any;
}

const getNewSet = (name: string, description: string, expr: SetExpr): Set => ({
  id: uuidv4(),
  type: "set",
  name,
  description: description || "",
  enabled: true,
  node_encoding: { type: "passthrough", color: "#FFFFFF" },
  edge_encoding: { type: "passthrough", color: "#FFFFFF" },
  expr,
  dataType: expr.type === 'gfql_chain' ? expr.dataType : 'string',
});

const getExpr = ({ type, dataType, nodes, edges }: CreateSetParams, gfqlString: string): SetExpr => ({
  type: type === 'gfql_chain' ? type : type === 'index_list' ? 'index_list' : 'no_op',
  ...(type === 'gfql_chain' ? { dataType, gfql: JSON.parse(gfqlString) } :
      type === 'index_list' ? { nodes, edges } : {})
});

const isUnsupportedDataType = (dataType: string | undefined): boolean => {
  if (!dataType) return false;
  return dataType === 'datetime' || dataType === 'date' || dataType === 'time';
};

const createSetFromProperty = (setName: string, setDescription: string, interactionDetails: InteractionDetails, gfqlString: string, collections: Collections): Record<string, Set> => {
  const newSet = getNewSet(setName, setDescription, getExpr(interactionDetails, gfqlString));
  const setsArray = Object.entries(collections?.sets || {})
    .filter(([key, value]) => key !== 'length' && key !== 'order' && value && value.id)
    .map(([_, set]) => set);
  return [newSet, ...setsArray].reduce((obj, set, i) => {
    obj[String(i)] = set;
    return obj;
  }, {} as Record<string, Set>);
};

export const SetModal: React.FC<SetModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  collections,
  interactionDetails,
  initialName = '',
  initialGfql = '[]',
  initialDescription = ''
}) => {
  const { componentType: rawComponentType, dataType, columns = [] } = interactionDetails;
  const componentType = rawComponentType === 'edge' ? 'edge' : 'node';
  const enableGfqlEditor = interactionDetails.type === 'gfql_chain' || interactionDetails.type === 'index_list';

  const {
    columnOptions,
    operatorsByDataType,
    showQueryBuilder,
    setShowQueryBuilder,
    builderCompatible,
    gfqlError,
    gfqlString,
    setName,
    setSetName,
    setDescription,
    setSetDescription,
    operations,
    findMatchingColumn,
    handleGfqlChange,
    handleOperationChange,
    handleConditionChange,
    handleAddCondition,
    handleRemoveCondition,
    handleAddOperation,
    handleRemoveOperation,
    handleCaseSensitiveChange
  } = useSetEditor({
    initialGfql,
    collections,
    interactionDetails,
    initialName,
    initialDescription,
    componentType,
    columns
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.stopPropagation();
    const newSetsObject = createSetFromProperty(setName, setDescription, interactionDetails, gfqlString, collections);
    onConfirm({ value: newSetsObject });
    onClose();
  };

  const hasUnsupportedDataType = useMemo(() =>
    operations.some(operation =>
      operation.conditions.some(c => c.attributeName && isUnsupportedDataType(findMatchingColumn(c.attributeName)?.dataType))
    ),
    [operations, findMatchingColumn]
  );

  if (!isOpen) return null;

  return createPortal(
    <div className={styles['modal-overlay']} onClick={(e) => { 
      if (e.target === e.currentTarget && window.getSelection().toString() === '') {
        e.stopPropagation(); 
        onClose(); 
      }
    }}>
      <div className={styles['modal-content']} onClick={(e) => e.stopPropagation()}>
        <h3 className={styles['modal-header']}>{initialName ? 'Edit Set' : 'Create Set'}</h3>
        <form id="set-modal-form" onSubmit={handleSubmit} className={styles['modal-body']}>
          <div className={styles['input-group']}>
            <label>Name</label>
            <input
              type="text"
              placeholder="Set Name"
              value={setName}
              onChange={(e) => setSetName(e.target.value)}
              className={styles['set-name-input']}
              autoFocus
            />
          </div>

          <div className={styles['input-group']}>
            <label>Description</label>
            <textarea
              placeholder="Enter description (optional)"
              value={setDescription}
              onChange={(e) => setSetDescription(e.target.value)}
              className={styles['set-description-input']}
              rows={2}
            />
          </div>

          {enableGfqlEditor && (
            <div className={styles['collection-query-section']}>
              <div className={styles['collection-query-header']}>
                <label>Collection Query</label>
                <label>Show Query</label>
                <button
                  type="button"
                  className={`${styles['show-query-toggle']} ${!showQueryBuilder ? styles['active'] : ''}`}
                  onClick={() => setShowQueryBuilder(!showQueryBuilder)}
                  title={showQueryBuilder ? 'Show Query (GFQL)' : 'Show Query Builder'}
                >
                  <span className={styles['toggle-thumb']} />
                </button>
              </div>

              {showQueryBuilder && !builderCompatible ? (
                <div className={styles['advanced-gfql-notice']}>
                  This collection uses advanced GFQL that cannot be represented by the visual builder. Use Show Query to view or edit it.
                </div>
              ) : showQueryBuilder ? (
                <GfqlBuilder
                  operations={operations}
                  onOperationChange={handleOperationChange}
                  onConditionChange={handleConditionChange}
                  onCaseSensitiveChange={handleCaseSensitiveChange}
                  onAddCondition={handleAddCondition}
                  onRemoveCondition={handleRemoveCondition}
                  onAddOperation={handleAddOperation}
                  onRemoveOperation={handleRemoveOperation}
                  columnOptions={columnOptions}
                  findMatchingColumn={findMatchingColumn}
                  operatorsByDataType={operatorsByDataType}
                />
              ) : (
                <GfqlRawEditor
                  gfqlString={gfqlString}
                  onGfqlChange={handleGfqlChange}
                  gfqlError={gfqlError}
                />
              )}
              </div>
          )}
        </form>
        <div className={styles['modal-footer']}>
          <button type="button" className={styles['cancel-button']} onClick={(e) => { e.stopPropagation(); onClose(); }}>Cancel</button>
          <button
            type="submit"
            form="set-modal-form"
            className={styles['create-button']}
            disabled={!setName.trim() || hasUnsupportedDataType}
          >
            {initialName ? 'Update Set' : 'Save Set'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
